import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'

import isUrl from 'is-url'
import queryString from 'qs'

import { getUrl, Urls } from 'app/UrlService'
import { UserApi } from 'data/api/userApi'
import { buildUrl } from 'data/utils/utils'
import analytics from 'utils/analytics'

import { Collapse, Flex } from 'v2/ui'
import useUrlParam from 'v2/ui/utils/useUrlParam'

import { Button, Input, Link, Text } from './AuthUIElements'
import EmailVerificationForm from './EmailVerificationForm'

const MODES = { EmailVerification: null, Password: 'password' }
const LoginForm = ({
    onSuccess,
    onRejected,
    email,
    onLogIn,
    showRegisterLink = false,
    invitation,
    forStack = null,
    openSignup = false,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [mode, setMode] = useUrlParam('mode', '')
    const [state, setState] = useState({ email: email || invitation?.user.email })
    const [error, setError] = useState(false)
    const [openSignupError, setOpenSignupError] = useState(false)

    useEffect(
        () => setState((state) => ({ ...state, email: email || invitation?.user.email })),
        [email, invitation]
    )

    const getRedirect = () => {
        let redirect = null
        const searchString = queryString.parse(window.location.search, {
            ignoreQueryPrefix: true,
        })
        if (searchString && searchString.redirect) {
            redirect = decodeURI(searchString.redirect)
        }

        return isUrl(redirect) && redirect
    }

    const getRedirectQuery = () => {
        let query = queryString.parse(window.location.search, {
            ignoreQueryPrefix: true,
        })
        if (query) {
            // drop these params as they aren't needed past the start
            // off onboarding
            delete query.email
            delete query.status
            delete query.mode

            query = queryString.stringify(query)
        }

        return query
    }

    const handleOpenSignup = (e) => {
        e.preventDefault()
        UserApi.handleOpenSignUp(invitation?.user?.email, forStack, invitation?.token)
            .then((response) => {
                if (
                    !(
                        response.success &&
                        (response.status == 'registration_confirmed' ||
                            'already_registered_skip_email')
                    )
                ) {
                    setOpenSignupError(true)
                    return
                }
                if (response.status == 'registration_confirmed') {
                    analytics.track('open signup registration completed', {
                        workspace_id: invitation?.workspace?._sid,
                        user_id: invitation?.user?._sid,
                        event_description: 'Open signup registration was completed',
                        is_new_user: invitation?.user?.is_new_user,
                        authentication_method: 'email',
                    })
                }

                handleLogin(e)
            })
            .catch((ex) => {
                console.error('Error during signup', ex)
                setOpenSignupError(true)
                return
            })
    }

    const handleLogin = (e) => {
        e.preventDefault()
        setIsLoading(true)
        setError(false)
        // If we have a redirect in the url, request it

        analytics.track('user login submitted', {
            event_category: 'user',
            event_description: 'User login was started',
            authentication_method: 'email',
        })

        const redirect = getRedirect()

        onLogIn(state.email, state.password, redirect)
            .then((response) => {
                if (!response.api_token) {
                    analytics.track('user login failed', {
                        event_category: 'user',
                        error: response.error,
                        event_description: 'User login failed',
                        authentication_method: 'email',
                    })

                    setError(true)
                    onRejected(response, state.email)
                    return
                }

                analytics.track('user login completed', {
                    event_category: 'user',
                    event_description: 'User login was completed',
                    authentication_method: 'email',
                })

                onSuccess(response)
            })
            .catch((ex) => {
                console.error('Error during login', ex)
                setError(true)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleChange = (e) => {
        const field = e.target.id
        const value = e.target.value

        setState((state) => ({ ...state, [field]: value }))
    }

    const toggleModeClick = (e) => {
        e.preventDefault()
        setMode(mode === MODES.Password ? MODES.EmailVerification : MODES.Password)
    }
    return (
        <>
            {mode !== MODES.Password && !invitation && (
                <Flex column align="stretch">
                    <EmailVerificationForm
                        onSuccess={onSuccess}
                        email={state.email}
                        onEmailChange={(email) => setState((state) => ({ ...state, email: email }))}
                    />
                    <Link mt={3} onClick={toggleModeClick}>
                        Login with password
                    </Link>
                </Flex>
            )}

            <Collapse isOpen={mode === MODES.Password || !!invitation}>
                <form
                    onSubmit={openSignup ? handleOpenSignup : handleLogin}
                    style={{ display: 'flex', flexDirection: 'column' }}
                >
                    <Input
                        variant="authentication"
                        id="email"
                        type="email"
                        placeholder="enter email"
                        width="100%"
                        mb={1}
                        value={state.email}
                        onChange={handleChange}
                        isDisabled={invitation}
                        aria-label="Email"
                    />
                    <Input
                        variant="authentication"
                        id="password"
                        type="password"
                        placeholder="enter password"
                        width="100%"
                        aria-label="Password"
                        mb={1}
                        value={state.password}
                        onChange={handleChange}
                    />
                    <Collapse isOpen={error}>
                        <Text variant="error" mb={1}>
                            Sorry, that information is not correct.
                        </Text>
                    </Collapse>
                    <Collapse isOpen={openSignupError}>
                        <Text variant="error" mb={1}>
                            Sorry, an error occurred registering you for this app.
                        </Text>
                    </Collapse>
                    <Button
                        width="100%"
                        buttonSize="sm"
                        mt={1}
                        mb={4}
                        isLoading={isLoading}
                        type="submit"
                    >
                        Sign in
                    </Button>

                    <Link
                        my={2}
                        to={`${buildUrl(getUrl(Urls.ResetPassword), {
                            email: state.email || '',
                        })}&${getRedirectQuery()}`}
                        style={{ alignSelf: 'center' }}
                    >
                        Forgot your password?
                    </Link>
                    {!invitation && (
                        <Link mt={2} onClick={toggleModeClick}>
                            Login without password
                        </Link>
                    )}
                </form>
            </Collapse>

            {!invitation && showRegisterLink && (
                <Link mt={1} to={`${getUrl(Urls.Register)}?${getRedirectQuery()}`}>
                    Create an account
                </Link>
            )}
        </>
    )
}
export default withRouter(LoginForm)
